import {environment} from '@environments/environment';
import {AuthRealm, BusinessContext, IOidcToken, OidcToken as LibOidcToken} from '@service-and-repairs/awpintegrationlib';
import {OidcConfigProvider} from '@oidc/OidcConfigProvider';
import {Util} from '../../util/util';

/**
 * This class fully resembles how we store WEN OIDC tokens in the browser's local storage.
 * The field names match the JSON structure from WEN, so the snake_case is intentional here.
 */
export class OidcToken {
  static readonly STORAGE_KEY: string = 'oidcToken';

  access_token: string; // Around 25-30 characters
  access_token_timestamp_ms: number; // Timestamp in milliseconds the access token was stored at (AWP specific)
  scope: string; // Something like "address b2d openid organization profile bmwids email b2xroles"
  id_token: string; // JWT holding basic identity information, but no roles
  token_type: string; // "Bearer"
  expires_in: number; // Not in use. Token will be refreshed based on environment specific expiration durations.
  access_token_refresh_timestamp_ms: number; // Timestamp in milliseconds the access token will be refreshed (AWP
                                             // specific)
  refresh_token: string; // Around 25-30 characters
  refresh_token_timestamp_ms: number;  // Timestamp in milliseconds the refresh token was stored at (AWP specific)
  business_context: string; // B2D | B2D_INTERNAL (AWP specific)

  static readFromStorage(b2xContext?: string): OidcToken {
    const parsedItemFromStorage = JSON.parse(OidcToken.readRawItemFromStorage(b2xContext));
    return Object.assign(new OidcToken(), parsedItemFromStorage);
  }

  /**
   * Returns whether any OIDC token (under the current or legacy storage key) is present in
   * storage, regardless of its content. Mirrors the presence check previously done directly
   * against storage, without requiring callers to inspect individual token fields.
   */
  static existsInStorage(b2xContext?: string): boolean {
    return !!OidcToken.readRawItemFromStorage(b2xContext);
  }

  private static readRawItemFromStorage(b2xContext?: string): string | null {
    return window.sessionStorage.getItem(OidcToken.STORAGE_KEY) || OidcToken.readFromLegacyStorage(b2xContext);
  }

  /**
   * TODO(ISPI-484050 follow-up): Temporary workaround for clients still running a stale,
   * browser/CDN-cached copy of assets/OidcFlow.js from before the OIDC token storage key was
   * unified to the static {@link OidcToken.STORAGE_KEY}. Such stale scripts store the token
   * under the old per-business-context key (e.g. 'oidcToken_B2D') instead, so it can no longer
   * be found by current code. Falls back to the legacy key, migrates the token to the new key,
   * and logs a warning so occurrences can be traced. Remove once telemetry confirms no more
   * legacy-keyed tokens occur in production.
   */
  private static readFromLegacyStorage(b2xContext?: string): string | null {
    const businessContext = b2xContext || (new OidcConfigProvider()).get(window.location.hostname)?.businessContext;
    if (!businessContext) {
      return null;
    }
    const legacyKey = 'oidcToken_' + businessContext;
    const legacyItem = window.sessionStorage.getItem(legacyKey);
    if (legacyItem) {
      console.warn(Util.logPrefix('Webapp', 'Auth')
        + `Found OIDC token under legacy storage key '${legacyKey}' instead of '${OidcToken.STORAGE_KEY}'. `
        + 'This indicates a stale cached OidcFlow.js was used during login. Migrating token to the new key.');
      window.sessionStorage.setItem(OidcToken.STORAGE_KEY, legacyItem);
      window.sessionStorage.removeItem(legacyKey);
    }
    return legacyItem;
  }

  static deleteFromStorage(_b2xContext?: string): void {
    window.sessionStorage.removeItem(OidcToken.STORAGE_KEY);
  }

  writeToStorage(): void {
    const storageValue = JSON.stringify(this);
    window.sessionStorage.setItem(OidcToken.STORAGE_KEY, storageValue);
  }

  deleteFromStorage(): void {
    OidcToken.deleteFromStorage();
  }

  getHeaders(): {[key: string]: string;} {
    const headers = {};
    headers['Auth-App-Id'] = 'awp';
    if (this.business_context) {
      headers['BusinessContext'] = this.business_context;
      headers['AuthScheme'] = this.business_context.substring(0, 3);
      headers['AuthRealm'] = this.getAuthRealm();
    }
    if (this.access_token) {
      headers['authorization'] = 'Bearer ' + this.access_token;
      headers['access_token'] = this.access_token; // No "Bearer" prefix to avoid conflicts with CSSL
      headers['AuthType'] = 'WEN_TOKEN';
    }
    return headers;
  }

  private getAuthRealm(): AuthRealm {
    if (environment.alphaRealmEnabled === true) {
      return AuthRealm.alpha;
    }
    return this.business_context === BusinessContext.B2D ? AuthRealm.internetb2x : AuthRealm.intranetb2x;
  }

  getAuthorizationHeader(): {[key: string]: string;} {
    return {authorization: 'Bearer ' + this.access_token};
  }

  toLibToken(): IOidcToken {
    const output: IOidcToken = new LibOidcToken();
    output.setAccessToken(this.access_token);
    output.setIdToken(this.id_token);
    output.setBusinessContext(this.business_context);
    output.setScopes(this.scope);
    output.setAccessTokenRefreshTimestamp(this.access_token_refresh_timestamp_ms);
    return output;
  }
}
